import { DateBox, Pagination } from "devextreme-react";
import { ScrollView } from "devextreme-react/scroll-view";
import { Item, Toolbar } from "devextreme-react/toolbar";
import notify from "../../lib/notify";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadSubmittingComments } from "../../api/settings";

export const ReadingNotes = () => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const formatDate = (date: Date): string => date.toISOString().split("T")[0];
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState<string>(formatDate(firstDayOfMonth));
  const [toDate, setToDate] = useState<string>(formatDate(lastDayOfMonth));
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0); // <-- ważne: DevExtreme startuje od 0
  const [totalItems, setTotalItems] = useState(0);

  const [submittingComments, setSubmittingComments] = useState<
    {
      id: number;
      companyName: string;
      title: string;
      description: string;
      dateCrerated: string;
    }[]
  >([]);

  useEffect(() => {
    fetchSubmittingComments();
  }, [fromDate, toDate, pageNumber]);

  const fetchSubmittingComments = async () => {
    try {
      setLoading(true);
      const response = await downloadSubmittingComments(
        fromDate,
        toDate,
        pageNumber,
      );
      if (response.isOk) {
        setSubmittingComments(response.data.items);
        setTotalItems(response.data.totalItems);
      } else {
        notify(
          `${t("Error:")} ${response.message || t("Unknown error")}`,
          "error",
        );
      }
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("readingNotes.header")}
        </div>
        <div className="mb-6 text-base">{t("readingNotes.helpMessage")}</div>
        <Toolbar className="mb-6">
          <Item location="center" locateInMenu="auto">
            <div className="flex flex-row justify-center gap-4">
              <DateBox
                value={fromDate}
                onValueChanged={(e) => setFromDate(e.value)}
                type="date"
                displayFormat="dd.MM.yyyy"
                label={t("submittingComments.fromDate")}
                labelMode="static"
                width={125}
              />
              <DateBox
                value={toDate}
                onValueChanged={(e) => setToDate(e.value)}
                type="date"
                displayFormat="dd.MM.yyyy"
                label={t("submittingComments.toDate")}
                labelMode="static"
                width={125}
              />
            </div>
          </Item>
        </Toolbar>

        <div className="mx-auto max-w-5xl flex flex-col gap-4 w-full">
          {submittingComments.length === 0 ? (
            <div
              className="p-4 mb-4 text-sm text-blue-800 rounded-lg bg-blue-50 dark:bg-gray-800 dark:text-blue-400"
              role="alert"
            >
              {t("readingNotes.noRecords")}
            </div>
          ) : (
            submittingComments.map((comment) => (
              <div
                key={comment.id}
                className="p-4 border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929]"
              >
                <div className="text-lg font-bold mb-1">
                  {`${comment.title} (${comment.companyName})`}
                </div>
                <div className="text-sm">{comment.description}</div>
              </div>
            ))
          )}
          <Pagination
            showInfo
            showNavigationButtons
            showPageSizeSelector={false}
            defaultPageSize={5}
            pageIndex={pageNumber}
            onPageIndexChange={(index) => setPageNumber(index)}
            itemCount={totalItems}
          />
        </div>
      </div>
    </ScrollView>
  );
};
